import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Link } from 'gatsby'
import odooImg   from '../images/services/61932592-01.webp'
import d365Img   from '../images/services/14224-01.webp'
import webImg    from '../images/services/4706200-01.webp'
import mobileImg from '../images/services/13_asadahasg1-01.webp'
import aiImg     from '../images/services/13728-01.webp'

const SECTION_COUNT = 3

/* Stacked fan of gradient-tinted rounded rects */
const ServiceVisual = ({ accentGlow }) => {
  const [r, g, b] = accentGlow.split(',').map(s => parseInt(s.trim()))
  const layers = [
    { shade: 0.10, angle: -28 },
    { shade: 0.22, angle: -18 },
    { shade: 0.38, angle: -10 },
    { shade: 0.56, angle: -3  },
    { shade: 0.76, angle:  6  },
    { shade: 1.00, angle: 15  },
  ]
  return (
    <svg
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}
      viewBox="0 0 300 220"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="300" height="220" fill="#07071a" />
      {layers.map(({ shade, angle }, i) => (
        <rect
          key={i}
          x="50" y="10" width="200" height="200" rx="34"
          fill={`rgb(${Math.round(r*shade)},${Math.round(g*shade)},${Math.round(b*shade)})`}
          transform={`rotate(${angle} 150 110)`}
        />
      ))}
    </svg>
  )
}

/* Card: text left (38 %) | visual right — img or SVG art */
const BrowserCard = ({ accentGlow, image, title, description, cta, link, logos, metrics, badge }) => (
  <div className="ss-card" style={{ '--accent': `rgb(${accentGlow})` }}>
    <div className="ss-card__inner">
      <div className="ss-card__info">
        <div className="ss-card__accent-bar" />
        <h3 className="ss-card__title">{title}</h3>
        <p className="ss-card__desc">{description}</p>
        <Link to={link} className="ss-card__cta-wrap">
          <button className="ss-card__cta">{cta}</button>
        </Link>
      </div>
      <div className="ss-card__visual">
        <img src={image} alt="" className="ss-card__visual-img" />
        {badge && <img src={badge} alt="" className="ss-card__badge" />}
        {/* ── Overlay: metrics anchored bottom-right of image ── */}
        <div className="ss-card__bottom">
          <div className="ss-card__metrics">
            {metrics.map((m, i) => (
              <div key={i} className="ss-card__metric">
                <span className="ss-card__metric-val">{m.value}</span>
                <span className="ss-card__metric-lbl">{m.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
)

const ScrollServices = ({ data }) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const wrapperRef = useRef(null)

  const sections = useMemo(() => [
    {
      label: 'ERP Implementation and Optimization',
      cards: [
        {
          accentGlow: '0, 180, 100',
          image: odooImg,
          badge: '/images/services/odoo_ready_partners_rgb-01.png',
          title: 'Certified Odoo Partner',
          description: 'AI-assisted Odoo implementations that automate workflows, cut deployment time, and scale with your business — delivered on time, every time.',
          cta: 'Explore',
          link: '/Odoo/implementationPartner',
          logos: [
            { initials: 'OD', bg: '#006d5b' },
            { initials: 'MS', bg: '#0073bf' },
            { initials: 'OM', bg: '#005f5f' },
          ],
          metrics: [
            { value: '15+',   label: 'Odoo Deployments' },
            { value: '6+',   label: 'Industries' },
            { value: '98%', label: 'On Time' },
          ],
        },
        {
          accentGlow: '0, 140, 255',
          image: d365Img,
          title: 'Microsoft Dynamics 365 Optimization Partner',
          description: 'AI-driven D365 optimizations that unlock the full Microsoft ecosystem — faster rollouts, smarter automation, and measurable ROI from day one.',
          cta: 'Explore',
          link: '/dynamics365/financeOperations',
          logos: [
            { initials: 'MS', bg: '#0052cc' },
            { initials: 'AX', bg: '#003f8a' },
            { initials: 'BC', bg: '#0078d4' },
          ],
          metrics: [
            { value: '5+', label: 'D365 Projects' },
            { value: '2+', label: 'Countries' },
            { value: '20+', label: 'Years Exp.' },
          ],
        },
      ],
    },
    {
      label: 'Custom Web and Mobile Development',
      cards: [
        {
          accentGlow: '0, 210, 255',
          image: webImg,
          title: 'Web Development, Reimagined with AI',
          description: 'AI-accelerated web development that ships enterprise-grade platforms faster — without compromising on performance, security, or user experience.',
          cta: 'Explore',
          link: '/services/webdevelopment',
          logos: [
            { initials: 'WB', bg: '#1a237e' },
            { initials: 'NX', bg: '#333333' },
            { initials: 'RE', bg: '#b71c1c' },
          ],
          metrics: [
            { value: '14+', label: 'Web Platforms' },
            { value: '10+',  label: 'Tech Stacks' },
            { value: '99%', label: 'Uptime' },
          ],
        },
        {
          accentGlow: '80, 220, 140',
          image: mobileImg,
          title: 'Mobile Apps, Accelerated by AI',
          description: 'AI-powered mobile development across Android and iOS — rapid delivery cycles, intelligent UX, and apps that perform from launch day.',
          cta: 'Explore',
          link: '/services/mobiledevelopment',
          logos: [
            { initials: 'AN', bg: '#1b5e20' },
            { initials: 'IO', bg: '#1c1c1e' },
            { initials: 'UM', bg: '#0f2d8e' },
          ],
          metrics: [
            { value: '5+',  label: 'Apps Launched' },
            { value: '2',   label: 'Platforms' },
            { value: '4.8★', label: 'Avg Rating' },
          ],
        },
      ],
    },
    {
      label: 'AI-Powered Solutions',
      cards: [
        {
          accentGlow: '160, 60, 255',
          image: aiImg,
          title: 'AI-Native Platforms',
          description: 'Purpose-built AI models, predictive analytics, and end-to-end automation — deployed at speed to turn your data into a real competitive edge.',
          cta: 'Explore',
          link: '/services/aisolution',
          logos: [
            { initials: 'PB', bg: '#f57f17' },
            { initials: 'ML', bg: '#7b1fa2' },
            { initials: 'DT', bg: '#00838f' },
          ],
          metrics: [
            { value: '10+', label: 'Models Finetuned' },
            { value: '3+',  label: 'Clients Served' },
            { value: '90%', label: 'Accuracy' },
          ],
        },
      ],
    },
  ], [])

  useEffect(() => {
    const headerEl = document.querySelector('.ant-layout-header')
    const headerH = headerEl ? headerEl.offsetHeight : 64

    const handleScroll = () => {
      const wrapper = wrapperRef.current
      if (!wrapper) return
      const rect = wrapper.getBoundingClientRect()
      const panelH = window.innerHeight - headerH
      const scrollable = wrapper.offsetHeight - panelH
      // how far we have scrolled past the stick point
      const scrolled = Math.max(0, headerH - rect.top)

      const newIndex = scrolled <= 0 ? 0
        : scrolled >= scrollable ? SECTION_COUNT - 1
        : Math.min(SECTION_COUNT - 1, Math.floor((scrolled / scrollable) * SECTION_COUNT))
      setActiveIndex(prev => prev !== newIndex ? newIndex : prev)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div ref={wrapperRef} id="services" className="scroll-services">
      <div className="scroll-services__sticky">

        {/* ── Full-width heading ── */}
        <div className="scroll-services__header">
          {/* <p className="scroll-services__eyebrow">What we do</p> */}
          <h2 className="scroll-services__heading">
            We Deliver with AI Speed.
          </h2>
          <span className="scroll-services__heading-accent"> Customer Satisfaction is Our Priority</span>
        </div>

        {/* ── Body: nav (left) + cards (right), both anchored to top ── */}
        <div className="scroll-services__body">
          <nav className="scroll-services__left">
            {sections.map((s, i) => (
              <div
                key={i}
                className={`scroll-services__nav-item${i === activeIndex ? ' is-active' : ''}`}
              >
                <span className="scroll-services__nav-num">{String(i + 1).padStart(2, '0')}</span>
                <span className="scroll-services__nav-label">{s.label}</span>
              </div>
            ))}
          </nav>

          <div className="scroll-services__right">
            {sections.map((section, i) => (
              <div
                key={i}
                className={`scroll-services__group${i === activeIndex ? ' is-active' : i < activeIndex ? ' is-above' : ''}`}
              >
                {section.cards.map((card, j) => (
                  <BrowserCard key={j} {...card} />
                ))}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  )
}

export default ScrollServices
